// src/context/ThemeContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';

export const THEME_COLORS = {
  LIGHT: {
    name: 'Light',
    primary: '#0d6efd',
    secondary: '#6f42c1',
    text: '#212529',
    background: '#f8f9fa',
    cardBackground: '#ffffff',
    border: '#dee2e6',
    muted: '#6c757d',
    success: '#198754',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#0dcaf0'
  },
  DARK: {
    name: 'Dark',
    primary: '#0d6efd',
    secondary: '#6f42c1',
    text: '#ffffff',
    background: '#121212',
    cardBackground: '#1e1e1e',
    border: '#333333',
    muted: '#6c757d',
    success: '#198754',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#0dcaf0'
  }
};

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      return savedTheme ? JSON.parse(savedTheme) : THEME_COLORS.LIGHT;
    } catch (error) {
      console.error('Failed to load theme from localStorage:', error);
      return THEME_COLORS.LIGHT;
    }
  });

  const toggleTheme = () => {
    setCurrentTheme(currentTheme === THEME_COLORS.LIGHT ? THEME_COLORS.DARK : THEME_COLORS.LIGHT);
  };

  useEffect(() => {
    try {
      localStorage.setItem('theme', JSON.stringify(currentTheme));
      
      // Set CSS custom properties for the entire app
      document.documentElement.style.setProperty('--primary-color', currentTheme.primary);
      document.documentElement.style.setProperty('--secondary-color', currentTheme.secondary);
      document.documentElement.style.setProperty('--text-color', currentTheme.text);
      document.documentElement.style.setProperty('--background-color', currentTheme.background);
      document.documentElement.style.setProperty('--card-background', currentTheme.cardBackground);
      document.documentElement.style.setProperty('--border-color', currentTheme.border);
      document.documentElement.style.setProperty('--muted-color', currentTheme.muted);
      document.documentElement.style.setProperty('--success-color', currentTheme.success);
      document.documentElement.style.setProperty('--warning-color', currentTheme.warning);
      document.documentElement.style.setProperty('--danger-color', currentTheme.danger);
      document.documentElement.style.setProperty('--info-color', currentTheme.info);
      
      // Set body background and text color
      document.body.style.backgroundColor = currentTheme.background;
      document.body.style.color = currentTheme.text;
      
    } catch (error) {
      console.error('Failed to save theme to localStorage:', error);
    }
  }, [currentTheme]);

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      setCurrentTheme, 
      toggleTheme, 
      THEME_COLORS,
      isDark: currentTheme === THEME_COLORS.DARK 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};